
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useQuery } from "@tanstack/react-query";
import { fetchServiceGaps } from "@/services/landexpand/landExpandService";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { PlusCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface GrowthOpportunityDetailsProps {
  organizationId: string;
}

export function GrowthOpportunityDetails({ organizationId }: GrowthOpportunityDetailsProps) {
  const navigate = useNavigate();
  
  const { data, isLoading, error } = useQuery({
    queryKey: ['serviceGaps', organizationId],
    queryFn: () => fetchServiceGaps(organizationId),
    enabled: !!organizationId
  });

  const handleCreateOpportunity = (serviceId: string, serviceName: string) => {
    navigate(`/opportunities/create`, { 
      state: { 
        organizationId: organizationId,
        serviceId: serviceId,
        serviceName: serviceName,
        opportunityType: 'expansion'
      } 
    });
  };
  
  if (isLoading) {
    return <Skeleton className="h-[300px] w-full" />;
  }

  if (error) {
    return <p className="text-sm text-muted-foreground">Failed to load service gaps</p>;
  }

  const existingServices = data?.services?.filter(service => service.status === 'active') || [];
  const potentialServices = data?.services?.filter(service => service.status === 'potential') || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Service Gap Analysis</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <h3 className="text-sm font-semibold mb-2">Current Services</h3>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Service</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Start Date</TableHead>
                <TableHead>Renewal Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {existingServices.length > 0 ? (
                existingServices.map((service) => (
                  <TableRow key={service.id}>
                    <TableCell className="font-medium">{service.name}</TableCell>
                    <TableCell>{service.category}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        Active
                      </Badge>
                    </TableCell>
                    <TableCell>{service.startDate || 'N/A'}</TableCell>
                    <TableCell>{service.renewalDate || 'N/A'}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-4 text-muted-foreground">
                    No active services
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <div>
          <h3 className="text-sm font-semibold mb-2">Potential Expansion Opportunities</h3>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Service</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Fit Score</TableHead>
                <TableHead>Recommendation</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {potentialServices.length > 0 ? (
                potentialServices.map((service) => (
                  <TableRow key={service.id}>
                    <TableCell className="font-medium">{service.name}</TableCell>
                    <TableCell>{service.category}</TableCell>
                    <TableCell>
                      <div className={cn(
                        "px-2 py-1 rounded-full text-xs font-medium w-fit",
                        service.fitScore >= 80 ? "bg-green-100 text-green-800" :
                        service.fitScore >= 60 ? "bg-blue-100 text-blue-800" :
                        "bg-gray-100 text-gray-800"
                      )}>
                        {service.fitScore}%
                      </div>
                    </TableCell>
                    <TableCell>{service.recommendation}</TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleCreateOpportunity(service.id, service.name)}
                      >
                        <PlusCircle className="h-4 w-4 mr-1" />
                        Add
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-4 text-muted-foreground">
                    No potential services identified
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
